/**
 * The one place an API leaderboard row becomes a row a game may see.
 *
 * Both ends of the bridge need this projection — the play shell, which fetches
 * the page on a hosted game's behalf, and `DirectTransport`, which fetches it
 * itself — and there must not be two of them. The difference between the two
 * shapes is not cosmetic: the API row carries a platform `userId` and a
 * `scoreSubmissionId`, and this function is where they are dropped.
 *
 * Dropping them is deliberate. A hosted game is third-party code running in a
 * sandbox (CLAUDE.md invariant 15); handing it a stable platform identifier for
 * every player on the board would be handing out something drawing a board does
 * not need, and once it is on the wire it is out of the platform's hands. What a
 * game actually wanted those ids for is "which row is mine", and that question
 * is answered here, on the trusted side, as `isMe`.
 *
 * Nothing about the track is taken from the response either. The track is the
 * session's, which the server decided at launch (invariants 1 and 2) — reading
 * it back out of a body would make the answer depend on which board was fetched
 * rather than on which session is playing.
 */
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function rowOf(value) {
    if (!isRecord(value))
        return null;
    const rank = Number(value['rank']);
    const scoreValue = Number(value['scoreValue']);
    if (!Number.isFinite(rank) || !Number.isFinite(scoreValue))
        return null;
    return {
        rank,
        userId: typeof value['userId'] === 'string' ? value['userId'] : null,
        displayName: typeof value['displayName'] === 'string' ? value['displayName'] : '',
        scoreValue,
        achievedAt: typeof value['achievedAt'] === 'string' ? value['achievedAt'] : '',
    };
}
function project(row, myUserId) {
    return {
        rank: row.rank,
        displayName: row.displayName,
        scoreValue: row.scoreValue,
        achievedAt: row.achievedAt,
        // A null `myUserId` is a player with no public row — a guest, in the usual
        // case. `null === null` would mark every anonymised row on the board as
        // theirs, so the comparison is guarded rather than written the short way.
        isMe: myUserId !== null && row.userId === myUserId,
    };
}
/**
 * Project one API page onto the game-facing shape.
 *
 * Tolerant of a malformed row rather than throwing: a board with one unreadable
 * entry should render the rest, because the alternative is a game showing an
 * error for a cosmetic surface.
 */
export function toLeaderboardPage(body, session) {
    const record = isRecord(body) ? body : {};
    const rawEntries = Array.isArray(record['entries']) ? record['entries'] : [];
    const mine = rowOf(record['myEntry']);
    const myUserId = mine?.userId ?? null;
    const entries = [];
    for (const raw of rawEntries) {
        const row = rowOf(raw);
        if (row !== null)
            entries.push(project(row, myUserId));
    }
    return {
        competitionId: session.competitionId,
        rankingTrack: session.rankingTrack,
        entries,
        nextCursor: typeof record['nextCursor'] === 'string' ? record['nextCursor'] : null,
        // `isMe` is forced true here rather than compared: this row *is* the
        // viewer's, by construction, and on a board where the viewer is anonymised
        // the id comparison would not say so.
        myEntry: mine === null ? null : { ...project(mine, myUserId), isMe: true },
    };
}
